"use client";

import { Header } from "@marketing-sites/ui/components";
import type { HeaderNavigation } from "@marketing-sites/ui/components";

import { useHeaderNavigation } from "@/components/global-sections/header/fixtures";
import { CrossAppLink } from "@/components/cross-app-link";

type DrlpHeaderProps = {
  /** Secondary nav items (links + final button), built by createDrlpHeaderCtas. */
  secondary: HeaderNavigation["secondary"];
  /** When true, the logo does not link to the homepage. Defaults to true. */
  disableLogoLink?: boolean;
  className?: string;
  /** True on the `/legacy-drlp` route, which renders this same header. */
  legacy: boolean;
};

/**
 * Page-owned header for intercom-com DRLP routes — the intercom analogue of
 * fin-ai's HeaderWrapper. The global (i2y4) layout Header steps aside on
 * `/drlp/*` (returns null), so this is the only header rendered on a DRLP page.
 * Rendering it in the page tree (server-resolved CTAs passed in) keeps A/B
 * selection on the server with no hydration flash.
 *
 * It reuses the shared intercom logo and shows no primary nav or mobile menu,
 * matching the stripped-down DRLP header.
 */
export function DrlpHeader({
  secondary,
  disableLogoLink = true,
  className,
  legacy,
}: DrlpHeaderProps) {
  const { intercomFixtureData } = useHeaderNavigation();
  const fixtureData: HeaderNavigation = {
    logo: intercomFixtureData.logo,
    primary: [],
    secondary,
  };

  return (
    <Header
      fixtureData={fixtureData}
      disableMobileMenu={true}
      disableLogoLink={disableLogoLink}
      LinkComponent={CrossAppLink}
      className={className}
      bannerLinkMetadata={{
        analyticsId: legacy
          ? "evt_drlp_legacy_nav_banner_link"
          : "evt_drlp_nav_banner_link",
      }}
      bannerCloseMetadata={{
        analyticsId: legacy
          ? "evt_drlp_legacy_nav_banner_close"
          : "evt_drlp_nav_banner_close",
      }}
      desktopLogoMetadata={{
        analyticsId: legacy
          ? "link_drlp_legacy_nav_desktop_logo"
          : "link_drlp_nav_desktop_logo",
      }}
      switchHoverCardMetadata={{
        analyticsId: legacy
          ? "link_drlp_legacy_nav_desktop_switch_hover_card"
          : "link_drlp_nav_desktop_switch_hover_card",
      }}
      mobileLogoMetadata={{
        analyticsId: legacy
          ? "link_drlp_legacy_nav_mobile_logo"
          : "link_drlp_nav_mobile_logo",
      }}
      mobileCrossSiteMetadata={{
        analyticsId: legacy
          ? "link_drlp_legacy_nav_mobile_cross_site"
          : "link_drlp_nav_mobile_cross_site",
      }}
    />
  );
}
